import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { capitalize } from 'lodash'
import {
  IconChevronRight,
  IconReportSearch,
  IconExclamationCircle,
  IconChevronDown,
  IconMessagePlus,
} from '@tabler/icons-react'

import { unscoreScorecard } from '@/reducers/scorecards/scorecards.actions'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Pill } from '@/components/pills/Pill'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { NotesPanel } from '@/components/notes/NotesPanel'
import { fetchQAScoresByCallId } from '@/reducers/callSearch/callSearch.actions'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'
import { CHANNEL_TYPE } from '@/utils/constants'

import { calculateGrade, formatScorePercent, roundTwoDecimals } from './helpers'

export const QAScore = ({
  scorecard,
  handleEditScore,
  totalScorecards,
  organizationId,
  callType = CHANNEL_TYPE.CALL,
}) => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(() => totalScorecards <= 2)
  const { possible_score: possibleScore, aggregate_score: aggregateScore } = scorecard
  const grade = calculateGrade(scorecard)
  const isScored = scorecard.scored_status === 'scored'
  const isPartiallyScored = scorecard.scored_status === 'partially_scored'
  const isUnscored = scorecard.scored_status === 'unscored'
  const isFullyManual = (scorecard) => {
    if (!scorecard.section_scores) {
      return false
    }

    return scorecard.section_scores.every((section) =>
      section.measure_scores?.every((measure) =>
        measure.criteria_scores?.every((criteria) => criteria.criteria_type === 'manual')
      )
    )
  }
  const [isUnscoreModalOpen, setIsUnscoreModalOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [unscoreError, setUnscoreError] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  // Scorecard note lives in a NotesPanel toggled from the header (next to
  // View Score), mirroring the criteria note. Auto-open when one exists.
  const [showNote, setShowNote] = useState(false)
  useEffect(() => {
    if (scorecard?.scorecard_notes?.some((note) => !note.deleted)) {
      setShowNote(true)
    }
  }, [])
  const toggleOpen = () => setIsOpen((open) => !open)
  const actions =
    scorecard.type === 'copilot'
      ? [
          {
            label: 'Unscore Call',
            onClick: () => setIsUnscoreModalOpen(true),
            testId: 'unscore-option',
            isDanger: true,
          },
        ]
      : []

  const renderUnscoredBanner = () => {
    const fullyManual = isFullyManual(scorecard)

    return (
      <Banner warning={fullyManual} informative={!fullyManual} data-testid="qa-unscored-banner">
        {fullyManual
          ? 'This scorecard contains only manual criteria. Please complete the manual review to finalize scoring.'
          : 'Scoring is currently in progress...'}
      </Banner>
    )
  }
  const handleConfirmUnscore = async () => {
    setIsDeleting(true)
    try {
      await dispatch(unscoreScorecard(scorecard.id, reason))
      await dispatch(fetchQAScoresByCallId(scorecard.call_id, organizationId))
      setIsUnscoreModalOpen(false)
      setReason('')
      setUnscoreError('')
    } catch (err) {
      setUnscoreError(err.message)
    } finally {
      setIsDeleting(false)
    }
  }
  return (
    <>
      <header>
        <h2>
          <div className="flex-align-center small-gap clickable" onClick={toggleOpen}>
            {isOpen ? (
              <IconChevronDown className="icon-svg" data-testid="collapse-icon" />
            ) : (
              <IconChevronRight className="icon-svg" data-testid="expand-icon" />
            )}
            <div className="flex-align-center medium-gap">
              <span>{scorecard.name}</span>
              <Pill
                small
                magical={scorecard.type === 'copilot'}
                success={scorecard.type === 'automated'}
              >
                {scorecard.type === 'copilot' ? 'Quality' : capitalize(scorecard.type)}
              </Pill>
              {scorecard.auto_failed && (
                <Pill small critical>
                  Auto-fail
                </Pill>
              )}
            </div>
          </div>
          <div className="flex-align-stretch small-gap">
            <Button
              square
              small
              onClick={() => setShowNote((open) => !open)}
              tooltip="Add a note"
              data-testid={`add-scorecard-note-button-${scorecard.id}`}
            >
              <IconMessagePlus />
            </Button>
            <Button
              small
              className="no-wrap"
              onClick={() => handleEditScore(scorecard)}
              data-testid={!isScored ? 'override-score-button' : 'view-score-details-button'}
            >
              {!isScored ? 'Edit Score' : 'View Score'}
            </Button>
            {actions.length > 0 && (
              <ActionsDropdown actions={actions} dataTestId="qa-scorecard-options-button" />
            )}
          </div>
        </h2>
      </header>
      {!isOpen && (
        <>
          {isPartiallyScored && (
            <Banner className="scorecard-banner" warning data-testid="qa-partially-scored-banner">
              <div className="qa-scorecard-banner">
                <p>
                  This {callType} was partially scored. Some criteria require manual review before
                  finalizing the score.
                </p>
              </div>
            </Banner>
          )}
          {isUnscored && renderUnscoredBanner()}
          {isScored && (
            <footer>
              <div>Overall Score</div>
              <Pill large data-testid="total-score" {...grade}>
                {roundTwoDecimals(aggregateScore)}/{possibleScore} points -{' '}
                {formatScorePercent(aggregateScore, possibleScore)}
              </Pill>
            </footer>
          )}
        </>
      )}
      {isOpen && (
        <>
          {scorecard.section_scores?.map((sectionScore) => {
            const { possible_score: sectionPossibleScore, aggregate_score: sectionAggregateScore } =
              sectionScore
            const hasManualCriteria = sectionScore.measure_scores?.some((measure) =>
              measure.criteria_scores?.some((criteria) => criteria.criteria_type === 'manual')
            )
            return (
              <section key={sectionScore.id}>
                <h2>
                  <div className="flex-align-center small-gap">
                    {sectionScore.name}
                    {(isUnscored || isPartiallyScored) && hasManualCriteria && (
                      <InteractiveTooltip content="Manual review required">
                        <IconReportSearch
                          className="status-warning"
                          data-testid="manual-review-icon"
                          size={18}
                          stroke={1.5}
                        />
                      </InteractiveTooltip>
                    )}
                  </div>
                </h2>
                <div data-testid="qa-section-score">
                  {isScored
                    ? `${roundTwoDecimals(sectionAggregateScore)}/${sectionPossibleScore}`
                    : '--'}
                </div>
              </section>
            )
          })}
          {isScored && (
            <footer>
              <div>Overall Score</div>
              <Pill large data-testid="qa-total-score" {...grade}>
                {roundTwoDecimals(aggregateScore)}/{possibleScore} points -{' '}
                {formatScorePercent(aggregateScore, possibleScore)}
              </Pill>
            </footer>
          )}
          {isPartiallyScored && (
            <>
              <footer>
                <div>Overall Score</div>
                <div data-testid="qa-total-score">
                  {isScored
                    ? `${roundTwoDecimals(aggregateScore)}/${possibleScore} points - ${formatScorePercent(aggregateScore, possibleScore)}`
                    : '--'}
                </div>
              </footer>
              <Banner warning data-testid="qa-partially-scored-banner">
                This {callType} was partially scored. Some criteria require manual review before
                finalizing the score.
              </Banner>
            </>
          )}
          {isUnscored && renderUnscoredBanner()}
        </>
      )}
      {showNote && (
        <NotesPanel
          anchor={{ noteType: 'scorecard', id: scorecard.id }}
          organizationId={organizationId}
          compact
          collapsible={false}
        />
      )}
      <BasicModal
        show={isUnscoreModalOpen}
        onClose={() => setIsUnscoreModalOpen(false)}
        canCloseOnDimmerClick={false}
        closeOnEscape={false}
        size="tiny"
        title={`Unscore Call for ${scorecard.name}`}
        footer={
          <div className="flex-align-center flex-end small-gap">
            {!unscoreError ? (
              <>
                <Button onClick={() => setIsUnscoreModalOpen(false)}>Cancel</Button>
                <Button
                  critical
                  data-testid="unscore-button"
                  disabled={reason.trim() === '' || isDeleting}
                  loading={isDeleting}
                  onClick={handleConfirmUnscore}
                >
                  Unscore
                </Button>
              </>
            ) : (
              <Button
                data-testid="unscore-modal-ok-button"
                brand
                onClick={() => setIsUnscoreModalOpen(false)}
              >
                Ok
              </Button>
            )}
          </div>
        }
      >
        {unscoreError ? (
          <Banner critical>
            <p>
              <b>This call cannot be unscored</b>
            </p>
            <p>{unscoreError}</p>
          </Banner>
        ) : (
          <>
            <Banner
              style={{ marginTop: 0 }}
              header="Proceed with Caution"
              icon={<IconExclamationCircle />}
              caution
            >
              Removing this scorecard will reset the call&apos;s score and remove any tasks
              generated from it. This action is monitored and permanent.
            </Banner>
            <div className="field-stack">
              <FormField>
                <label htmlFor="unscore-reason">
                  Please enter a reason for removing this evaluation
                </label>
                <TextInput
                  id="unscore-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </FormField>
            </div>
          </>
        )}
      </BasicModal>
    </>
  )
}
